import React, { FC, useContext, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "../../../css/ImageGallery.scss";
import { Icon } from "@chakra-ui/react";
import { ChevronRightIcon } from "@chakra-ui/icons";
import { ImagePageContext } from "../../CreateContext";
import { Movie } from "./Body";

type Props = {
  Medias: {
    COMMENT: string;
    DISPLAY_SIZE: string;
    ID: number;
    OWNER_TOPIC_ID: number;
    TITLE: string;
    TYPE: string;
    URL: string;
  }[];
  parentHeight: number;
  ModalLayout: number;
  onClose: () => void;
  modalCanCloseFlag?: boolean
  setMoveEndFlag: (MoveEndFlag: boolean) => void
};

export const ImageGallery: FC<Props> = (props) => {
  const { Medias, parentHeight, ModalLayout, onClose, setMoveEndFlag, modalCanCloseFlag } = props;
  const [images, setImages] = useState<{ src: string; size: string; type: string }[]>([]);
  const [loadedMedia, setLoadedMedia] = useState(false);
  const { ImagePage } = useContext(ImagePageContext)
  const [[page, direction], setPage] = useState([ImagePage.page, 0]);
  const variants = {
    enter: (direction: number) => {
      return {
        x: direction > 0 ? 100 : -100,
        opacity: 0,
      };
    },
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
    },
    exit: (direction: number) => {
      return {
        zIndex: 0,
        x: direction < 0 ? 100 : -100,
        opacity: 0,
      };
    },
  };

  const swipeConfidenceThreshold = 10000;

  useEffect(() => {
    const loadImages = async () => {
      const loadedImages: { src: string; size: string; type: string }[] = [];

      if (!Medias || Medias.length === 0) {
        // Mediasが空またはundefinedの場合
        loadedImages.push({ src: "none", size: "none", type: "none" });
      } else {
        for (const media of Medias) {
          if (media.TYPE === "image") {
            const img = new Image();
            img.src = media.URL;

            await new Promise<void>((resolve) => {
              img.onload = () => {
                if (img.width > img.height) {
                  const VirtualHeight = window.innerWidth * 0.46 * img.height / img.width;
                  if (VirtualHeight > parentHeight - 32) {
                    loadedImages.push({ src: img.src, size: 'tatenaga', type: "image" });
                  } else {
                    loadedImages.push({ src: img.src, size: 'yokonaga', type: "image" });
                  }
                } else {
                  loadedImages.push({ src: img.src, size: 'tatenaga', type: "image" });
                }
                resolve();
              };
            });
          } else if (media.TYPE === "video") {
            loadedImages.push({ src: media.URL, size: 'video', type: "video" });
          }
        }
      }

      setImages(loadedImages);
      setLoadedMedia(true);
    };

    loadImages();
  }, []);

  const swipePower = (offset: number, velocity: number) => {
    return Math.abs(offset) * velocity;
  };

  const imageIndex = (page: number) => {
    return (page % images.length + images.length) % images.length;
  };

  const paginate = (newDirection: number) => {
    setPage([page + newDirection, newDirection]);
  };



  if (!loadedMedia) {
    return null; // 読み込み中の場合は何も表示しない
  } else {
    return (
      <div className="image-gallery-container">
        <AnimatePresence initial={false} custom={direction}>
          <motion.div
            className="image"
            style={{
              width: `${images[imageIndex(page)].size}` == "yokonaga" ? "100%" : "auto",
              height: `${images[imageIndex(page)].size}` == "tatenaga" ? "100%" : "auto",
            }}
            key={page}
            custom={direction}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              x: { type: 'spring', stiffness: 300, damping: 30 },
              opacity: { duration: 0.2 },
            }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={1}
            onDragEnd={(e, { offset, velocity }) => {
              const swipe = swipePower(offset.x, velocity.x);

              if (swipe < -swipeConfidenceThreshold) {
                paginate(1);
              } else if (swipe > swipeConfidenceThreshold) {
                paginate(-1);
              }
            }}
          >
            {images[imageIndex(page)].type == "image" ? (
              <motion.img
                style={{
                  width: `${images[imageIndex(page)].size}` == "yokonaga" ? "100%" : "auto",
                  height: `${images[imageIndex(page)].size}` == "tatenaga" ? "100%" : "auto",
                }}
                src={images[imageIndex(page)].src}
              />) : images[imageIndex(page)].type === "movie" ? (
                <Movie
                  ModalLayout={ModalLayout}
                  onClose={onClose}
                  setMoveEndFlag={setMoveEndFlag}
                  modalCanCloseFlag={modalCanCloseFlag}
                  url={images[imageIndex(page)].src}
                  CardHeight={parentHeight}
                />) : images[imageIndex(page)].type === "none" ? (
                  null
                ) : null
            }
          </motion.div>
        </AnimatePresence>

        {images.length > 1 && (
          <div className="dot-list">
            {images.map((_, index) => (
              <div
                key={index}
                className={`dot ${index === imageIndex(page) ? 'active' : ''}`}
              />
            ))}
          </div>
        )}

        {images.length > 1 && (
          <>
            <div className="next" onClick={() => paginate(-1)}>
              <Icon as={ChevronRightIcon} />
            </div>
            <div className="prev" onClick={() => paginate(1)}>
              <Icon as={ChevronRightIcon} />
            </div>
          </>
        )}
      </div>
    );
  };
}
